import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DownOutlined } from '@ant-design/icons';
import './RoleDropdown.scss';

interface Role {
  key: string;
  label: string;
  isNewSpeaker?: boolean;
}

interface RoleDropdownProps {
  roles: Role[];
  selectedRole: string;
  onRoleSelect: (role: string, speakerId?: string) => void;
  onNewSpeaker?: () => void;
  showNewSpeaker?: boolean;
  editingRoleKey?: string | null;
  editingRoleName?: string;
  onStartEditRole?: (roleKey: string, currentName: string) => void;
  onSaveRoleEdit?: (roleKey: string) => Promise<void>;
  onCancelRoleEdit?: () => void;
  onEditingRoleNameChange?: (name: string) => void;
  onDeleteRole?: (roleKey: string) => void; // 删除说话人
  isSeriesSpeaker?: boolean; // 是否为剧集台词
  disabled?: boolean; // 是否禁用
  onDisabledClick?: () => void; // 禁用状态下的点击回调
}

const RoleDropdown: React.FC<RoleDropdownProps> = ({
  roles,
  selectedRole,
  onRoleSelect,
  onNewSpeaker,
  showNewSpeaker = true,
  editingRoleKey,
  editingRoleName = '',
  onStartEditRole,
  onSaveRoleEdit,
  onCancelRoleEdit,
  onEditingRoleNameChange,
  onDeleteRole,
  isSeriesSpeaker = false,
  disabled = false,
  onDisabledClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubMenuKey, setOpenSubMenuKey] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ left: number; top: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  // 角色颜色配置
  const roleColors = [
    { background: '#00aeb41a', color: '#00b5bd' },  // 第1个角色 青
    { background: '#7271F117', color: '#7271F1' },  // 第2个角色 紫
    { background: '#FCCA0721', color: '#DFB20A' },  // 第3个角色   深黄
    { background: '#ff4d4f2e', color: '#ff4d4f' }, // 第4个角色  红
    { background: '#e9f5e9', color: '#3b9f3c' }, // 第5个角色  绿
    { background: '#06a2d11a', color: '#06a2d1' },  // 第6个角色  蓝
    { background: '#ffb36612', color: '#f7a552' } // 第7个角色  橙
  ];

  // 获取角色样式
  const getRoleStyle = (roleName: string) => {
    // 如果是剧集音色，使用固定颜色
    if (isSeriesSpeaker) {
      return {
        background: '#0039b31a',
        color: '#0039b3'
      };
    }
    
    // 找到角色在列表中的索引
    const roleIndex = roles.findIndex(role => role.label === roleName);
    
    if (roleIndex === -1) return roleColors[0];
    
    // 循环使用颜色（排除"警察"等默认角色）
    const regularRoleIndex = roleIndex % roleColors.length;
    return roleColors[regularRoleIndex];
  };

  // 使用ref来避免闭包问题
  const editingRoleKeyRef = useRef(editingRoleKey);
  const onSaveRoleEditRef = useRef(onSaveRoleEdit);
  
  // 更新ref值
  useEffect(() => {
    editingRoleKeyRef.current = editingRoleKey;
    onSaveRoleEditRef.current = onSaveRoleEdit;
  });

  // 点击外部关闭下拉框（同时关闭操作框）：仅当点击不在「触发按钮、主菜单、操作子菜单」内时才关闭，避免点击同行空白（布局上可能在 role-dropdown 盒内）无法关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      const inSubmenu = submenuRef.current?.contains(target);
      if (!inTrigger && !inMenu && !inSubmenu) {
        setOpenSubMenuKey(null);
        setSubmenuPosition(null);
        // 如果正在编辑，先保存编辑
        if (editingRoleKeyRef.current) {
          onSaveRoleEditRef.current?.(editingRoleKeyRef.current);
        }
        setIsOpen(false);
      }
    };

    // 只有在下拉框打开时才添加事件监听器
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // 只依赖isOpen，避免因editingRoleKey变化重新绑定

  const updateSubmenuPosition = useCallback(() => {
    if (!menuRef.current || !openSubMenuKey) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const activeItem = menuRef.current.querySelector('.role-dropdown-item-active') as HTMLElement | null;
    const top = activeItem ? activeItem.getBoundingClientRect().top : menuRect.top;
    setSubmenuPosition({ left: menuRect.right + 4, top });
  }, [openSubMenuKey]);

  // 操作框定位：在说话人列表右侧，使用 fixed 避免撑开列表滚动
  useEffect(() => {
    if (!openSubMenuKey || !menuRef.current) {
      setSubmenuPosition(null);
      return;
    }
    updateSubmenuPosition();
  }, [openSubMenuKey, updateSubmenuPosition]);

  const closeSubmenu = useCallback(() => {
    setOpenSubMenuKey(null);
    setSubmenuPosition(null);
  }, []);

  // 角色列表（role-dropdown-menu）滚动时，操作框自动消失
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu || !openSubMenuKey) return;
    const handleMenuScroll = () => closeSubmenu();
    menu.addEventListener('scroll', handleMenuScroll);
    return () => menu.removeEventListener('scroll', handleMenuScroll);
  }, [openSubMenuKey, closeSubmenu]);

  // 台词列表滚动时，操作框自动消失
  useEffect(() => {
    if (!openSubMenuKey || !dropdownRef.current) return;
    let scrollParent: HTMLElement | null = dropdownRef.current;
    while (scrollParent) {
      const { overflowY, overflow } = getComputedStyle(scrollParent);
      if (overflowY === 'auto' || overflowY === 'scroll' || overflow === 'auto' || overflow === 'scroll') {
        scrollParent.addEventListener('scroll', closeSubmenu, true);
        return () => scrollParent?.removeEventListener('scroll', closeSubmenu, true);
      }
      scrollParent = scrollParent.parentElement;
    }
    window.addEventListener('scroll', closeSubmenu, true);
    return () => window.removeEventListener('scroll', closeSubmenu, true);
  }, [openSubMenuKey, closeSubmenu]);

  // 动态计算菜单位置，使其显示在触发按钮下方
  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      // 获取触发按钮的位置
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const containerRect = dropdownRef.current?.getBoundingClientRect();
      
      if (containerRect) {
        // 计算菜单相对于容器的位置
        // 菜单的 top = 触发按钮底部相对于容器的位置 + 间距
        const topOffset = triggerRect.bottom - containerRect.top + 4;
        menuRef.current.style.top = `${topOffset}px`;
        // 菜单的 left 与触发按钮对齐
        menuRef.current.style.left = `${triggerRect.left - containerRect.left}px`;
      }
    }
  }, [isOpen]);

  // 保存当前编辑并切换到指定角色的编辑状态（用于点击其他说话人编辑时）
  const handleSaveAndSwitchEdit = async (role: Role) => {
    if (editingRoleKey) {
      try {
        await onSaveRoleEdit?.(editingRoleKey);
      } catch (err) {
        console.error('保存角色编辑失败:', err);
        return;
      }
    }
    onStartEditRole?.(role.key, role.label);
  };

  const handleRoleClick = (role: Role, e?: React.MouseEvent) => {
    if (disabled) {
      if (onDisabledClick) {
        onDisabledClick();
      }
      return;
    }
    // 若当前正在编辑其他说话人，点击本行视为“保存当前并进入本说话人编辑”，不视为选中
    if (editingRoleKey && editingRoleKey !== role.key) {
      e?.stopPropagation();
      handleSaveAndSwitchEdit(role);
      return;
    }
    // 若点击的正是当前在编辑的那一行，不触发选中/关闭
    if (editingRoleKey === role.key) {
      e?.stopPropagation();
      return;
    }
    // 从role.key中提取speaker_id
    let speakerId = null;
    if (role.key.startsWith('speaker_')) {
      speakerId = role.key.replace('speaker_', '');
      console.log('🎭 选择的说话人ID:', speakerId, '说话人名称:', role.label);
    } else {
      console.log('🎭 选择的角色名称:', role.label, '(非API创建的说话人，无speaker_id)');
    }
    onRoleSelect(role.label, speakerId || undefined);
    setIsOpen(false);
  };

  const handleNewSpeakerClick = async () => {
    if (disabled) {
      if (onDisabledClick) {
        onDisabledClick();
      }
      return; // 如果禁用，调用禁用回调后返回
    }
    
    if (onNewSpeaker) {
      await onNewSpeaker();
      // 创建完成后不关闭下拉框，保持打开状态以便用户修改名称
      // setIsOpen(false); // 注释掉这行，保持下拉框打开
    }
  };

  const currentStyle = getRoleStyle(selectedRole);

  return (
    <div className="role-dropdown" ref={dropdownRef}>
      <div 
        ref={triggerRef}
        className="role-dropdown-trigger"
        onClick={() => {
          if (isSeriesSpeaker) {
            return; // 剧集台词不处理点击
          }
          if (disabled) {
            if (onDisabledClick) {
              onDisabledClick();
            }
            return; // 禁用状态下调用禁用回调
          }
          setIsOpen(!isOpen);
        }}
        style={{
          backgroundColor: currentStyle.background,
          color: currentStyle.color,
          borderLeft: isSeriesSpeaker ? '4px solid #0039b3' : 'none',
          cursor: isSeriesSpeaker || disabled ? 'default' : 'pointer',
          width: '100px',
          minWidth: '100px',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <span 
          className="role-label" 
          style={{ 
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            textAlign: 'center',
            width: isSeriesSpeaker ? '100%' : 'calc(100% - 20px)',
            marginRight: isSeriesSpeaker ? '0' : '10px'
          }}
          title={selectedRole}
        >
          {selectedRole}
        </span>
        {!isSeriesSpeaker && <DownOutlined className={`dropdown-icon ${isOpen ? 'open' : ''}`} />}
      </div>
      
             {isOpen && !isSeriesSpeaker && (
         <>
         <div ref={menuRef} className="role-dropdown-menu">
                       {roles.map((role, index) => (
              <div
                key={role.key}
                className={`role-dropdown-item${openSubMenuKey === role.key ? ' role-dropdown-item-active' : ''}`}
                onClick={(e) => handleRoleClick(role, e)}
                style={{
                  backgroundColor: getRoleStyle(role.label).background,
                  color: getRoleStyle(role.label).color
                }}
              >
                                 {editingRoleKey === role.key ? (
                   <input
                     type="text"
                     value={editingRoleName}
                     onChange={(e) => onEditingRoleNameChange?.(e.target.value)}
                     aria-label="编辑角色名称"
                     onKeyDown={async (e) => {
                       if (e.key === 'Enter') {
                         e.preventDefault();
                         try {
                           await onSaveRoleEdit?.(role.key);
                           // 说话人保存不关闭下拉框，仅选中项时才关闭
                         } catch (error) {
                           console.error('保存角色编辑失败:', error);
                         }
                       } else if (e.key === 'Escape') {
                         onCancelRoleEdit?.();
                         setIsOpen(false); // 取消编辑时关闭下拉框
                       }
                     }}
                     onBlur={async () => {
                       try {
                         await onSaveRoleEdit?.(role.key);
                         // 说话人保存不关闭下拉框，仅选中项时才关闭
                       } catch (error) {
                         console.error('保存角色编辑失败:', error);
                       }
                     }}
                     className="role-edit-input"
                     autoFocus
                     onClick={(e) => e.stopPropagation()}
                   />
                 ) : (
                  <span 
                    className="role-item-label"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '80px',
                      display: 'inline-block',
                      textAlign: 'center'
                    }}
                    title={role.label}
                  >
                    {role.label}
                  </span>
                )}
                {editingRoleKey !== role.key && (
                  <div className="role-item-actions">
                    <button
                      type="button"
                      className="role-more-btn"
                      aria-label="更多操作"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenSubMenuKey(prev => prev === role.key ? null : role.key);
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true" className="role-more-icon-vertical">
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
           
                       {showNewSpeaker && (
              <div
                className="role-dropdown-item new-speaker"
                onClick={handleNewSpeakerClick}
                style={{
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  justifyContent: 'center',
                }}
              >
                <span 
                  className="role-item-label"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '80px',
                    display: 'inline-block',
                    textAlign: 'center'
                  }}
                  title="新建说话人"
                >
                  新建说话人
                </span>
              </div>
            )}
         </div>

         {/* 操作框：在列表右侧独立浮层，不参与列表滚动 */}
         {openSubMenuKey && submenuPosition && (() => {
           const role = roles.find(r => r.key === openSubMenuKey);
           if (!role) return null;
           return (
             <div
               ref={submenuRef}
               className="role-submenu role-submenu-right"
               role="menu"
               style={{
                 position: 'fixed',
                 left: submenuPosition.left,
                 top: submenuPosition.top,
                 zIndex: 1001
               }}
               onClick={(e) => e.stopPropagation()}
             >
               <div
                 className="role-submenu-item"
                 role="menuitem"
                 onClick={() => {
                   handleSaveAndSwitchEdit(role);
                   setOpenSubMenuKey(null);
                 }}
               >
                 重命名
               </div>
               <div
                 className="role-submenu-item danger"
                 role="menuitem"
                 onClick={() => {
                   onDeleteRole?.(role.key);
                   setOpenSubMenuKey(null);
                   setIsOpen(false);
                 }}
               >
                 删除
               </div>
             </div>
           );
         })()}
         </>
       )}
    </div>
  );
};

export default RoleDropdown;
